import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

/**
 * Tema de la aplicación. Controla la clase `.dark` en el <html> para activar
 * el modo oscuro definido por Tailwind en index.css.
 *
 * Estrategia de hidratación (evita parpadeo FOUC):
 *  - En build/runtime, `index.html` ejecuta un script inline mínimo que lee
 *    `localStorage` ANTES de que React monte y aplica `.dark` al <html>.
 *    (Ese script vive en index.html).
 *  - El ThemeProvider lee el mismo storage al arrancar y sincroniza
 *    futuros cambios.
 *
 * Preferencia respetada: usuario explícito > prefers-color-scheme del SO.
 */

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'sb_theme';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  // Sin preferencia explícita — respeta el SO.
  const prefersDark =
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

function applyThemeClass(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    applyThemeClass(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* storage deshabilitado (modo privado, etc.) — no rompemos la app */
    }
  }, [theme]);

  // Reacciona a cambios de preferencia del SO si el usuario no eligió explícito.
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const explicit = window.localStorage.getItem(THEME_STORAGE_KEY);
      if (explicit === 'light' || explicit === 'dark') return; // respeta elección
      setThemeState(e.matches ? 'dark' : 'light');
    };
    // addEventListener no existe en todos los navegadores antiguos para MediaQueryList
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Fallback Safari antiguo
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
    [],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, toggleTheme, setTheme }),
    [theme, toggleTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}